import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Wind,
  Activity,
  Droplets,
  HeartPulse,
  Bone,
  AlertOctagon,
  Award,
  CheckCircle,
  ChevronRight,
  ArrowLeft,
  Shield,
  Hand,
} from "lucide-react";
import api from "../api/axios";
import StickyCallBar from "../components/shared/StickyCallBar";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const DRABC_STEPS = [
  {
    id: "danger",
    icon: AlertTriangle,
    color: "bg-red-600",
    borderColor: "border-red-200",
    bgColor: "bg-red-50",
    titleEn: "D - DANGER",
    titleHi: "D - खतरा",
    descEn:
      "Check if the area is safe for you to approach. Look for oncoming traffic, fire, electrical hazards, or unstable structures. Do NOT become a second victim.",
    descHi:
      "जांचें कि क्या क्षेत्र आपके पास जाने के लिए सुरक्षित है। आने वाले यातायात, आग, बिजली के खतरों या अस्थिर संरचनाओं की तलाश करें। दूसरा शिकार न बनें।",
    actions: [
      "Look for traffic from all directions",
      "Check for fire or smoke nearby",
      "Look for fallen electrical wires",
      "Make the area safe if possible (turn on hazard lights, place warning signs)",
    ],
    actionsHi: [
      "सभी दिशाओं से यातायात देखें",
      "आस-पास आग या धुएं की जांच करें",
      "गिरे हुए बिजली के तारों की तलाश करें",
      "यदि संभव हो तो क्षेत्र को सुरक्षित बनाएं (हैजार्ड लाइट चालू करें)",
    ],
  },
  {
    id: "response",
    icon: Hand,
    color: "bg-orange-600",
    borderColor: "border-orange-200",
    bgColor: "bg-orange-50",
    titleEn: "R - RESPONSE",
    titleHi: "R - प्रतिक्रिया",
    descEn:
      "Gently tap the person's shoulders and ask loudly: 'Are you okay? Can you hear me?' If no response, shout for help from bystanders.",
    descHi:
      "व्यक्ति के कंधों को धीरे से थपथपाएं और जोर से पूछें: 'क्या आप ठीक हैं? क्या आप मुझे सुन सकते हैं?' यदि कोई प्रतिक्रिया नहीं है, तो आसपास के लोगों से मदद के लिए चिल्लाएं।",
    actions: [
      "Tap both shoulders gently but firmly",
      "Speak loudly and clearly near both ears",
      "Check for any visible injuries",
      "If responsive, ask them to stay still and wait for help",
    ],
    actionsHi: [
      "दोनों कंधों को धीरे से लेकिन मजबूती से थपथपाएं",
      "दोनों कानों के पास जोर से और स्पष्ट रूप से बोलें",
      "किसी भी दिखाई देने वाली चोट की जांच करें",
      "यदि प्रतिक्रिया दे रहे हैं, तो उन्हें स्थिर रहने और मदद की प्रतीक्षा करने के लिए कहें",
    ],
  },
  {
    id: "airway",
    icon: Wind,
    color: "bg-teal-600",
    borderColor: "border-teal-200",
    bgColor: "bg-teal-50",
    titleEn: "A - AIRWAY",
    titleHi: "A - वायुमार्ग",
    descEn:
      "If the person is unconscious, tilt their head back gently by placing two fingers under the chin and lifting. This opens the airway. Remove any visible obstructions from the mouth (blood, vomit, debris).",
    descHi:
      "यदि व्यक्ति बेहोश है, तो ठुड्डी के नीचे दो उंगलियां रखकर धीरे से सिर को पीछे झुकाएं। यह वायुमार्ग खोलता है। मुंह से दिखाई देने वाली किसी भी रुकावट (खून, उल्टी, मलबा) को हटाएं।",
    actions: [
      "Place one hand on forehead",
      "Lift chin with two fingers of other hand",
      "Tilt head back gently",
      "Look inside mouth for obstructions - remove carefully",
    ],
    actionsHi: [
      "एक हाथ माथे पर रखें",
      "दूसरे हाथ की दो उंगलियों से ठुड्डी उठाएं",
      "सिर को धीरे से पीछे झुकाएं",
      "मुंह के अंदर रुकावट देखें - सावधानी से हटाएं",
    ],
  },
  {
    id: "breathing",
    icon: Activity,
    color: "bg-blue-600",
    borderColor: "border-blue-200",
    bgColor: "bg-blue-50",
    titleEn: "B - BREATHING",
    titleHi: "B - श्वास",
    descEn:
      "With the airway open, check for breathing for 10 seconds: LOOK at the chest for rise and fall, LISTEN near the nose/mouth for breath sounds, FEEL for air on your cheek.",
    descHi:
      "वायुमार्ग खुला रखते हुए, 10 सेकंड तक सांस की जांच करें: छाती के उठने-गिरने को देखें, नाक/मुंह के पास सांस की आवाज सुनें, अपने गाल पर हवा महसूस करें।",
    actions: [
      "LOOK: Watch the chest for 10 seconds",
      "LISTEN: Put your ear near their mouth",
      "FEEL: Check for air flow on your cheek",
      "If NOT breathing → Start CPR immediately",
    ],
    actionsHi: [
      "देखें: 10 सेकंड तक छाती देखें",
      "सुनें: अपना कान उनके मुंह के पास रखें",
      "महसूस करें: अपने गाल पर हवा का प्रवाह जांचें",
      "यदि सांस नहीं चल रही → तुरंत सीपीआर शुरू करें",
    ],
  },
  {
    id: "circulation",
    icon: Droplets,
    color: "bg-red-700",
    borderColor: "border-red-300",
    bgColor: "bg-red-50",
    titleEn: "C - CIRCULATION",
    titleHi: "C - रक्त संचार",
    descEn:
      "Check for severe bleeding. If blood is flowing, apply DIRECT and CONTINUOUS pressure with a clean cloth. Do NOT remove the cloth - add more layers on top if blood soaks through.",
    descHi:
      "गंभीर रक्तस्राव की जांच करें। यदि खून बह रहा है, तो साफ कपड़े से सीधा और निरंतर दबाव लगाएं। कपड़ा न हटाएं - यदि खून भिगो दे तो ऊपर और परतें जोड़ें।",
    actions: [
      "Scan entire body for bleeding wounds",
      "Apply firm pressure with clean cloth",
      "DO NOT remove the cloth - add more layers",
      "Elevate bleeding limbs above heart level if possible",
    ],
    actionsHi: [
      "पूरे शरीर में रक्तस्राव के घाव की जांच करें",
      "साफ कपड़े से मजबूत दबाव लगाएं",
      "कपड़ा न हटाएं - और परतें जोड़ें",
      "यदि संभव हो तो रक्तस्राव वाले अंगों को हृदय स्तर से ऊपर उठाएं",
    ],
  },
];

const INTERVENTION_CARDS = [
  {
    id: "cpr",
    icon: HeartPulse,
    color: "bg-red-600",
    titleEn: "CPR - If Not Breathing",
    titleHi: "सीपीआर - यदि सांस नहीं",
    steps: [
      "Place the heel of your hand on the CENTER of the chest",
      "Interlock your other hand on top",
      "Push HARD and FAST - at least 5cm deep",
      "30 compressions, then 2 rescue breaths",
      "Rate: 100-120 compressions per minute",
      "Continue until ambulance arrives",
    ],
    stepsHi: [
      "अपने हाथ की एड़ी को छाती के बीच में रखें",
      "दूसरा हाथ ऊपर इंटरलॉक करें",
      "जोर से और तेज दबाएं - कम से कम 5 सेमी गहरा",
      "30 दबाव, फिर 2 बचाव श्वास",
      "गति: प्रति मिनट 100-120 दबाव",
      "एम्बुलेंस आने तक जारी रखें",
    ],
  },
  {
    id: "bleeding",
    icon: Droplets,
    color: "bg-orange-600",
    titleEn: "Bleeding Control",
    titleHi: "रक्तस्राव नियंत्रण",
    steps: [
      "Apply DIRECT, CONTINUOUS pressure with clean cloth",
      "Do NOT remove the cloth - add more layers",
      "If limb bleeding, elevate above heart level",
      "For arterial bleeding (bright red, spurting): Apply maximum pressure",
      "Improvise bandage with dupatta, shirt, or any clean fabric",
    ],
    stepsHi: [
      "साफ कपड़े से सीधा, निरंतर दबाव लगाएं",
      "कपड़ा न हटाएं - और परतें जोड़ें",
      "यदि अंग से रक्तस्राव हो, हृदय स्तर से ऊपर उठाएं",
      "धमनी रक्तस्राव (चमकदार लाल) के लिए: अधिकतम दबाव लगाएं",
      "दुपट्टा, शर्ट या किसी साफ कपड़े से पट्टी बनाएं",
    ],
  },
  {
    id: "fracture",
    icon: Bone,
    color: "bg-teal-600",
    titleEn: "Fracture Support",
    titleHi: "फ्रैक्चर सहायता",
    steps: [
      "DO NOT try to straighten or move the injured limb",
      "Support the limb in the position you find it",
      "Use a wooden block, umbrella, or rolled newspaper as splint",
      "Tie gently above and below the fracture site",
      "Check circulation in fingers/toes below the injury",
    ],
    stepsHi: [
      "घायल अंग को सीधा करने या हिलाने की कोशिश न करें",
      "जिस स्थिति में मिले उसी स्थिति में अंग को सहारा दें",
      "लकड़ी के ब्लॉक, छतरी या मुड़े हुए अखबार को स्प्लिंट के रूप में उपयोग करें",
      "फ्रैक्चर स्थल के ऊपर और नीचे धीरे से बांधें",
      "चोट के नीचे उंगलियों/पैर की उंगलियों में रक्त संचार जांचें",
    ],
  },
];

const GuidedRescuePage = () => {
  const { locketId } = useParams();
  const id = locketId;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const reportId = searchParams.get("reportId");

  const [completedSteps, setCompletedSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [showInterventions, setShowInterventions] = useState(false);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [sessionStartTime] = useState(Date.now());
  const [sessionId, setSessionId] = useState(null);
  const [emergencyType, setEmergencyType] = useState(null);
  const stepRefs = useRef([]);

  useEffect(() => {
    const createSession = async () => {
      if (!reportId) return;
      try {
        const res = await api.post(`/emergency-report/${reportId}/rescue-session`);
        if (res.data?.id) setSessionId(res.data.id);
        if (res.data?.emergencyType) setEmergencyType(res.data.emergencyType);
      } catch (err) {
        console.error("Failed to create rescue session:", err);
      }
    };
    createSession();
  }, [reportId]);

  const markStepComplete = async (stepId) => {
    if (completedSteps.includes(stepId)) return;
    const updated = [...completedSteps, stepId];
    setCompletedSteps(updated);

    if (activeStep < DRABC_STEPS.length - 1) {
      setActiveStep(activeStep + 1);
      stepRefs.current[activeStep + 1]?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setShowInterventions(true);
    }

    if (sessionId) {
      const rewardEligible = updated.length >= 3;
      const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
      try {
        await api.put(`/emergency-report/rescue-session/${sessionId}`, {
          stepsCompleted: updated,
          rewardEligible,
          durationSeconds: duration,
        });
      } catch (err) {
        console.error("Failed to update session:", err);
      }
    }
  };

  const rewardEligible = completedSteps.length >= 3;

  // Reorder intervention cards based on emergency type
  const getOrderedInterventions = () => {
    if (!emergencyType) return INTERVENTION_CARDS;
    const order = emergencyType === "medical" ? ["cpr", "bleeding", "fracture"] : ["bleeding", "cpr", "fracture"];
    return [...INTERVENTION_CARDS].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  };
  const orderedInterventions = getOrderedInterventions();

  const emergencyTypeBanner = {
    medical: { text: t("rescue.typeMedical"), bg: "bg-red-500", border: "border-red-400" },
    accident: { text: t("rescue.typeAccident"), bg: "bg-orange-500", border: "border-orange-400" },
    fire: { text: t("rescue.typeFire"), bg: "bg-amber-500", border: "border-amber-400" },
  };

  const publicUrl = `${window.location.origin}/${id}/public`;
  const whatsappMsg = encodeURIComponent(
    `🚨 Emergency alert! Someone needs help. Scan their QR code for medical info: ${publicUrl}`
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="sticky top-0 z-40 bg-slate-900 text-white shadow-md">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <BrandLogo variant="light" className="flex-shrink-0" />
          <button onClick={() => navigate(`/${id}`)} className="p-1 text-white hover:bg-white/10 rounded-lg transition-colors" aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">{t("rescue.title")}</p>
            <p className="text-xs text-white/70">{t("rescue.subtitle")}</p>
          </div>
          <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1 text-xs font-semibold border border-white/20">
            <CheckCircle size={12} />
            {completedSteps.length}/{DRABC_STEPS.length}
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 pt-3">
        {/* Emergency type banner */}
        {emergencyType && emergencyTypeBanner[emergencyType] && (
          <div className={`${emergencyTypeBanner[emergencyType].bg} rounded-lg p-3 mb-2 shadow-md`}>
            <p className="text-sm font-bold text-white text-center">{emergencyTypeBanner[emergencyType].text}</p>
          </div>
        )}

        {/* SAFETY WARNING — must be first */}
        <div className="bg-red-600 rounded-lg p-3 mb-2 shadow-md shadow-red-600/20">
          <div className="flex items-center gap-2 text-white">
            <AlertOctagon size={18} className="shrink-0" />
            <p className="text-sm font-bold">{t("rescue.neckWarning")}</p>
          </div>
        </div>

        {/* WhatsApp notify button */}
        <a
          href={`https://wa.me/?text=${whatsappMsg}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-sm py-3 rounded-lg mb-2 shadow-md transition-colors duration-150"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          {t("rescue.whatsappNotify")}
        </a>

        <h2 className="text-base font-bold text-slate-900 mb-0.5">{t("rescue.primarySurveyTitle")}</h2>
        <p className="text-xs text-slate-500 mb-3">{t("rescue.primarySurveySubtitle")}</p>

        <div className="space-y-2 mb-4">
          {DRABC_STEPS.map((drabcStep, idx) => {
            const Icon = drabcStep.icon;
            const isCompleted = completedSteps.includes(drabcStep.id);
            const isActive = idx === activeStep && !isCompleted;
            const _ = idx > activeStep && !completedSteps.includes(DRABC_STEPS[idx - 1]?.id); // eslint-disable-line no-unused-vars

            return (
              <div
                key={drabcStep.id}
                ref={(el) => (stepRefs.current[idx] = el)}
                className={`rounded-lg border overflow-hidden transition-colors duration-150 ${
                  isCompleted
                    ? "bg-emerald-50 border-emerald-200"
                    : isActive
                      ? `${drabcStep.bgColor} ${drabcStep.borderColor} border-2 shadow-md`
                      : "bg-white border-slate-200 opacity-60"
                }`}
              >
                <div className={`px-3 py-3 ${isActive ? drabcStep.color + " text-white" : ""}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isCompleted
                          ? "bg-emerald-600"
                          : isActive
                            ? "bg-white/20"
                            : "bg-slate-100"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle size={20} className="text-white" />
                      ) : (
                        <Icon size={20} className={isActive ? "text-white" : "text-slate-400"} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-bold ${isActive ? "text-white" : isCompleted ? "text-emerald-800" : "text-slate-700"}`}>
                        {"DRABC"[idx]} - {t(`rescue.${drabcStep.id}`)}
                      </p>
                    </div>
                    {isCompleted && (
                      <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">{t("rescue.completed")}</span>
                    )}
                  </div>
                </div>

                {(isActive || isCompleted) && (
                  <div className="px-3 py-3">
                    <p className="text-xs text-slate-700 leading-relaxed mb-3">{t(`rescue.${drabcStep.id}Desc`)}</p>

                    <div className="space-y-1.5 mb-3">
                      {(lang === "hi" ? drabcStep.actionsHi : drabcStep.actions).map((action, aIdx) => (
                        <div key={aIdx} className="flex items-start gap-2">
                          <ChevronRight size={14} className="text-slate-400 shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-700">{action}</p>
                        </div>
                      ))}
                    </div>

                    {!isCompleted && (
                      <button
                        onClick={() => markStepComplete(drabcStep.id)}
                        className={`w-full flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg transition-colors duration-150 ${drabcStep.color}`}
                      >
                        <CheckCircle size={18} />
                        {t("rescue.markAsDone")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Reward banner — below DRABC steps */}
        <div
          className={`rounded-lg p-3 mb-3 border transition-colors duration-150 ${
            rewardEligible ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Award size={16} className={rewardEligible ? "text-emerald-600" : "text-amber-600"} />
            <p className={`text-xs font-bold ${rewardEligible ? "text-emerald-800" : "text-amber-800"}`}>
              {rewardEligible ? t("rescue.rewardBanner") : t("rescue.completeMoreSteps").replace("{{n}}", 3 - completedSteps.length)}
            </p>
          </div>
        </div>

        {(showInterventions || completedSteps.length >= 3) && (
          <div className="animate-fade-in">
            <h2 className="text-lg font-bold text-slate-900 mb-1">{t("rescue.criticalInterventions")}</h2>
            <p className="text-sm text-slate-500 mb-4">{t("rescue.goldenHourSubtitle")}</p>

            <div className="space-y-2 mb-4">
              {orderedInterventions.map((card, cardIdx) => {
                const Icon = card.icon;
                const isOpen = activeIntervention === card.id || (activeIntervention === null && cardIdx === 0);

                return (
                  <div key={card.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                    <button
                      onClick={() => setActiveIntervention(isOpen ? null : card.id)}
                      className={`w-full px-3 py-3 flex items-center gap-3 text-left transition-colors ${
                        isOpen ? card.color + " text-white" : "hover:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          isOpen ? "bg-white/20" : card.color
                        }`}
                      >
                        <Icon size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-bold ${isOpen ? "text-white" : "text-slate-900"}`}>
                          {t(`rescue.${card.id}Title`)}
                        </p>
                      </div>
                      <ChevronRight
                        size={18}
                        className={`shrink-0 transition-transform ${isOpen ? "rotate-90 text-white" : "text-slate-400"}`}
                      />
                    </button>

                    {isOpen && (
                      <div className="px-3 py-3 animate-fade-in">
                        <p className="text-xs text-slate-700 leading-relaxed mb-2">{t(`rescue.${card.id}Desc`)}</p>
                        <div className="space-y-2">
                          {(lang === "hi" ? card.stepsHi : card.steps).map((s, sIdx) => (
                            <div key={sIdx} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-xs font-bold text-slate-600">
                                {sIdx + 1}
                              </div>
                              <p className="text-sm text-slate-700">{s}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-blue-200 p-4 text-center mt-4">
          <Shield size={16} className="text-blue-500 mx-auto mb-2" />
          <p className="text-xs text-slate-500 leading-relaxed">{t("rescue.disclaimer")}</p>
          <p className="text-xs text-slate-400 mt-1">{t("rescue.poweredBy")}</p>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <StickyCallBar />
    </div>
  );
};

export default GuidedRescuePage;
